import { Box, Button, Flex, MenuItem, Text, useToast, VStack } from "@chakra-ui/react";
import { FilePdf, Sparkle } from "@phosphor-icons/react";
import issbAPI from "api/issb/issb";
import questionService from "api/issb/question.service";
import { RightSidebarHeader } from "components/Layout/RightSidebarWidget";
import { routes } from "config/paths";
import DevOnly from "context/Auth/DevOnly";
import { useGenerateDisclosureReport } from "hooks/query";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { showSuccessToast } from "utils";
import { markdownToHtml } from "utils/text-utils";
import LocalSpinner from "components/LocalSpinner";
import { parseFilteredKeywords } from "../../../Disclosure/components/DisclosureHelper";
import { getLatestAIDraft } from "../../AnswerDetailPage";
import ManageAnswer from "../ManageAnswer";
import { getStatusFromActivity } from "../AnswerActivityOneEvent";

export const MULTIPLE_ANSWERS_MARKER = "multiple";

/**
 * ??
 * Design: https://www.figma.com/design/uDql1qJTNBIvGnFDsOpWV4/ESG360-AIR?node-id=3248-60282&t=doNbk2kJuSHCk8iD-1
 *
 * NOte: This can be so slow that it appears buggy!
 * That is probably due to slow rendering in the DataInputPage.
 *
 * @param {string} filterBy
 * @param {function} setFilterBy
 * @param {array} fullList This lets us find the attributes that are available to filter by
 * @returns
 */
const BulkActionsComponent = ({ filteredQuestionList, filteredKeywords }) => {
	// make a composite shared-values "question"
  const questionData = {};

  for (const question of filteredQuestionList) {
    Object.keys(question).forEach((key) => {
      const v = question[key];
      if (v === null || v === undefined || v === "") {
        return;
      }
      if (!questionData[key]) {
        questionData[key] = v;
      }
      if (questionData[key] !== v) {
        questionData[key] = MULTIPLE_ANSWERS_MARKER;
      }
    });
  }
  const n = filteredQuestionList?.length;

  const toast = useToast();
  const [isApplying, setIsApplying] = useState(false);
  const [results, setResults] = useState({});
  const doApplyLatestAIDrafts = async () => {
	setIsApplying(true);
	setResults({});
	let allPromises = [];
	for (const question of filteredQuestionList) {
		// get history for AI draft
		const activities = await issbAPI.getActivities({ id: question.id });
		const latestAIDraft = getLatestAIDraft(activities);
		// apply
		if ( ! latestAIDraft) continue;
		const newQuestion = { ...question };
		newQuestion._answerParts = null; // clear out parsed version, if any
		newQuestion.answer = markdownToHtml(latestAIDraft.newValue);
		newQuestion.confidence = getStatusFromActivity("confidence", latestAIDraft); 
		newQuestion.completeness = getStatusFromActivity("completeness", latestAIDraft);
		const qPromise = questionService
				.update(question.id, newQuestion, {
					params: { draft: false, byAI: true, success: true },
				}).then(() => {
					setResults(prev => ({ ...prev, [question.id]: true }));
				});		
		allPromises.push(qPromise);
	}
	await Promise.all(allPromises);
	console.log("Applied latest AI drafts to", results, "questions");	
	showSuccessToast(toast, "Applied latest AI drafts to "+allPromises.length+"questions");
	setIsApplying(false);
  }

  return (
    <VStack align="start" spacing={8} ml={2}>
      <RightSidebarHeader>Select Actions</RightSidebarHeader>
      <Text>To apply to all of the {n} questions selected.</Text>
      <ManageAnswer questionData={questionData} questionIds={filteredQuestionList.map((q) => q.id)} />
		<DevOnly>
			<Button variant="secondary" onClick={doApplyLatestAIDrafts} isDisabled={isApplying}>
				(Experimental) Batch Apply Latest AI Drafts
			</Button>
			{results && Object.keys(results).length > 0 && <Text>Applied to: {Object.keys(results).length}</Text>}
			{results && Object.keys(results).length > 0 && ! isApplying && <Text>Now reload the page to refresh question data</Text>}
		</DevOnly>
    </VStack>
  );
};

export function RunAIButton({ questionnaireId, questions }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  if (!questionnaireId) {
    console.log("No questionnaireId found");
    return null;
  }
  const handleRunAI = () => {
    // go to ai review
    // Note: pass along the question filters
    const filterBy = searchParams.get("q") || "";
    navigate(routes.aiReview + (filterBy ? "?q=" + filterBy : ""));
  };
  if (!questionnaireId || !questions?.length) {
    console.log("No questions or questionnaireId", questionnaireId, questions);
  }

  return (
    <MenuItem icon={<Sparkle size={16} />} isDisabled={!questions?.length} onClick={() => handleRunAI()}>
      <Flex align="start" direction="column" gap={2} width="100%">
        <Box>AI Review</Box>
      </Flex>
    </MenuItem>
  );
}

export const RunDisclosureButton = ({ questionnaireId, filters }) => {
  const selectedFilters = parseFilteredKeywords(filters);

  const [isLoading, setIsLoading] = useState(false);

  const config = {
    onMutate: () => setIsLoading(true),
    onSuccess: (data) => {
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    onError: (error) => {
      console.error("Error generating report:", error);
    },
    onSettled: () => setIsLoading(false),
  };

  const { mutate: generateReport } = useGenerateDisclosureReport(config);

  const handleCreate = () => {
    const selectedFiltersPayload = {};
    Object.keys(selectedFilters).forEach((filter) => {
      // console.log("selected fitler", selectedFilters[filter].length)
      if (selectedFilters[filter].length > 0) {
        selectedFiltersPayload[filter] = Array.from(selectedFilters[filter]);
      }
    });

    generateReport({
      questionnaireId,
      filters: selectedFiltersPayload,
    });
  };

  return (
    <MenuItem icon={<FilePdf size={16} />} isDisabled={isLoading} onClick={() => handleCreate()}>
      <Flex direction="row" gap={2} width="100%">
        <Box>Create Report</Box>
        {isLoading && <LocalSpinner w="24px" size="sm" />}
      </Flex>
    </MenuItem>
  );
};

export default BulkActionsComponent;
